// ============================================================
// NM Social — intrarea compozitorului V2: cinci feluri de material
//
// Tiparul e luat din NM Write, unde se folosește de luni de zile: Text, Link,
// Foto, Doc, Voce. Aici e rescris pentru React, dar mecanica e aceeași, ca să
// nu existe două comportamente diferite pentru același lucru.
//
// CE FACE FIECARE, și ce costă:
//   Text — nimic special, textul e chiar materialul.
//   Link — adresa se ține aici; citirea paginii se face pe server (browserul nu
//          are voie să descarce un site străin). Ruta vine în pasul B.
//   Foto — poza trece prin `laJpeg`, aceeași care micșorează la încărcare, apoi
//          se transformă în base64 pentru blocul de imagine al lui Claude.
//   Doc  — .docx se citește AICI, în browser, cu mammoth; .txt la fel, direct;
//          PDF-ul NU se despachetează, merge întreg la Claude, care îl citește
//          nativ. Asta e și metoda din NM Write.
//   Voce — dictare din browser, Web Speech API. NU e transcriere plătită: nu
//          pleacă niciun fișier nicăieri, nu costă nimic, și scrie în aceeași
//          casetă de text pe care o poți corecta după.
//
// Nimic de aici nu cheamă Claude. Materialul se strânge și așteaptă butonul de
// generare, care e o decizie separată fiindcă fiecare apăsare costă bani.
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import mammoth from 'mammoth';
import { t } from './i18n';
import { laJpeg } from './mediaV2';
import { supabase } from './supabaseClient';

export const FELURI = ['text', 'link', 'foto', 'doc', 'voce'];

// Semnele sunt luate ca atare din NM Write, ca cele doua unelte sa arate la fel.
// Desenate cu linie, nu umplute, si mostenesc culoarea textului (`currentColor`),
// deci se coloreaza singure cand tabul e ales.
const SEMNE = {
  text: <path d="M4 6h16M4 12h16M4 18h10" />,
  link: <><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" /></>,
  foto: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="m3 17 5-4 4 3 3-2 6 5" /></>,
  doc: <><path d="M14 3v5h5" /><path d="M15 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M9 13h6M9 17h4" /></>,
  voce: <><rect x="9" y="3" width="6" height="12" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></>,
};

function Semn({ fel }) {
  return (
    <svg className="v2-intrare-semn" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {SEMNE[fel]}
    </svg>
  );
}

// Limita e a lui Claude pentru documente, nu a noastră: peste ea răspunde cu
// eroare, și e mai bine să afle omul aici decât după ce apasă Generează.
const DOC_MAX_MB = 30;

function base64Din(fisier) {
  return new Promise((rezolva, respinge) => {
    const cititor = new FileReader();
    // Rezultatul vine ca „data:image/jpeg;base64,...". Claude vrea doar coada.
    cititor.onload = () => rezolva(String(cititor.result).split(',')[1] || '');
    cititor.onerror = () => respinge(new Error('citire eșuată'));
    cititor.readAsDataURL(fisier);
  });
}

function textDin(fisier) {
  return new Promise((rezolva, respinge) => {
    const cititor = new FileReader();
    cititor.onload = () => rezolva(String(cititor.result || ''));
    cititor.onerror = () => respinge(new Error('citire eșuată'));
    cititor.readAsText(fisier);
  });
}

// Dictarea. Browserul o are sau nu o are; pe Chrome și pe Safari merge, pe
// Firefox nu. Când nu există, butonul nu se desenează deloc — un buton care nu
// poate funcționa e mai rău decât lipsa lui.
function motorDictare() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

// Ruta intoarce codul, nu propozitia: mesajul se scrie aici, ca sa fie in limba
// pe care o are interfata. Orice cod necunoscut cade pe cel general.
const ERORI_LINK = {
  lipseste_adresa: 'inLinkErGol',
  adresa_invalida: 'inLinkErAdresa',
  protocol_nepermis: 'inLinkErAdresa',
  pagina_nu_raspunde: 'inLinkErRaspuns',
  nu_e_pagina: 'inLinkErTip',
  pagina_goala: 'inLinkErGoala',
  prea_lent: 'inLinkErLent',
  neautentificat: 'inLinkErSesiune',
};

export default function IntrareV2({ valoare, onSchimba }) {
  const { fel, text, link, linkCitit, foto, doc } = valoare;
  const [dicteaza, setDicteaza] = useState(false);
  const [eroare, setEroare] = useState(null);
  const [lucreaza, setLucreaza] = useState(false);
  const recunoastere = useRef(null);
  const textLaPornire = useRef('');

  const schimba = camp => onSchimba({ ...valoare, ...camp });

  // Dictarea se oprește când pleci din compozitor, altfel microfonul rămâne
  // pornit și browserul ține indicatorul roșu aprins.
  useEffect(() => () => {
    if (recunoastere.current) { try { recunoastere.current.stop(); } catch (e) { /* deja oprită */ } }
  }, []);

  function comutaDictarea() {
    if (dicteaza) {
      try { recunoastere.current.stop(); } catch (e) { /* deja oprită */ }
      return;
    }
    const Motor = motorDictare();
    if (!Motor) return;
    const r = new Motor();
    r.lang = 'ro-RO';
    r.continuous = true;
    r.interimResults = true;
    textLaPornire.current = text ? text.trim() + ' ' : '';
    r.onresult = e => {
      let final = '';
      let provizoriu = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const bucata = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += bucata + ' '; else provizoriu += bucata;
      }
      if (final) textLaPornire.current += final;
      schimba({ text: (textLaPornire.current + provizoriu).replace(/\s+/g, ' ').trimStart() });
    };
    r.onerror = e => { setEroare(t('compozitor2', 'inDictareEroare')); setDicteaza(false); };
    r.onend = () => setDicteaza(false);
    recunoastere.current = r;
    setEroare(null);
    r.start();
    setDicteaza(true);
  }

  async function citesteLinkul() {
    if (!link || !link.trim()) return;
    setEroare(null);
    setLucreaza(true);
    try {
      // Ruta cere om logat (16 septembrie 2026): trimitem tokenul sesiunii.
      const { data: sesiuneCurenta } = await supabase.auth.getSession();
      const token = sesiuneCurenta && sesiuneCurenta.session ? sesiuneCurenta.session.access_token : '';
      const raspuns = await fetch('/api/citeste-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: link.trim() }),
      });
      const date = await raspuns.json();
      if (date.error) {
        setEroare(t('compozitor2', ERORI_LINK[date.error] || 'inLinkErGeneral'));
        schimba({ linkCitit: null });
        return;
      }
      schimba({ linkCitit: date });
    } catch (e) {
      setEroare(t('compozitor2', 'inLinkErGeneral'));
    } finally {
      setLucreaza(false);
    }
  }

  async function preiaFoto(fisier) {
    if (!fisier) return;
    setEroare(null);
    setLucreaza(true);
    try {
      // Aceeași trecere ca la media: convertit în JPEG și micșorat. O poză de
      // telefon trimisă întreagă ar umfla degeaba cererea către Claude.
      const { fisier: pregatit } = await laJpeg(fisier);
      const date = await base64Din(pregatit);
      schimba({ foto: { nume: fisier.name, date, previzualizare: URL.createObjectURL(pregatit) } });
    } catch (e) {
      setEroare(t('compozitor2', 'inFotoEroare'));
    } finally {
      setLucreaza(false);
    }
  }

  async function preiaDoc(fisier) {
    if (!fisier) return;
    setEroare(null);
    if (fisier.size / (1024 * 1024) > DOC_MAX_MB) {
      setEroare(t('compozitor2', 'inDocPreaMare', { mb: DOC_MAX_MB }));
      return;
    }
    const nume = fisier.name.toLowerCase();
    setLucreaza(true);
    try {
      if (nume.endsWith('.pdf')) {
        // PDF-ul merge întreg la Claude, care îl citește nativ, cu pagini cu tot.
        schimba({ doc: { nume: fisier.name, fel: 'pdf', date: await base64Din(fisier), text: null } });
      } else if (nume.endsWith('.docx')) {
        const rezultat = await mammoth.extractRawText({ arrayBuffer: await fisier.arrayBuffer() });
        const continut = (rezultat && rezultat.value ? rezultat.value : '').trim();
        if (!continut) { setEroare(t('compozitor2', 'inDocGol')); return; }
        schimba({ doc: { nume: fisier.name, fel: 'docx', date: null, text: continut } });
      } else if (nume.endsWith('.txt')) {
        const continut = (await textDin(fisier)).trim();
        if (!continut) { setEroare(t('compozitor2', 'inDocGol')); return; }
        schimba({ doc: { nume: fisier.name, fel: 'txt', date: null, text: continut } });
      } else {
        setEroare(t('compozitor2', 'inDocTip'));
      }
    } catch (e) {
      setEroare(t('compozitor2', 'inDocEroare'));
    } finally {
      setLucreaza(false);
    }
  }

  const areDictare = !!motorDictare();

  return (
    <div className="v2-intrare">
      <div className="v2-intrare-taburi" role="tablist">
        {FELURI.map(f => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={fel === f}
            className={`v2-intrare-tab${fel === f ? ' on' : ''}`}
            onClick={() => { setEroare(null); schimba({ fel: f }); }}
          >
            <Semn fel={f} />
            {t('compozitor2', 'in' + f.charAt(0).toUpperCase() + f.slice(1))}
          </button>
        ))}
      </div>

      {(fel === 'text' || fel === 'voce') && (
        <>
          <textarea
            className="textarea"
            rows={5}
            placeholder={t('compozitor2', fel === 'voce' ? 'inVocePlaceholder' : 'cePostamPlaceholder')}
            value={text}
            onChange={e => schimba({ text: e.target.value })}
          />
          {fel === 'voce' && (
            areDictare ? (
              <div className="v2-dictare">
                <button type="button" className={`v2-btn-media mic${dicteaza ? ' v2-rec' : ''}`} onClick={comutaDictarea}>
                  {dicteaza ? t('compozitor2', 'inVoceStop') : t('compozitor2', 'inVoceStart')}
                </button>
                <span className="v2-hint" style={{ margin: 0 }}>
                  {dicteaza ? t('compozitor2', 'inVoceAscult') : t('compozitor2', 'inVoceHint')}
                </span>
              </div>
            ) : (
              <p className="v2-hint">{t('compozitor2', 'inVoceLipsa')}</p>
            )
          )}
        </>
      )}

      {fel === 'link' && (
        <>
          <div className="v2-link-rand">
            <input
              className="v2-input"
              type="url"
              inputMode="url"
              placeholder={t('compozitor2', 'inLinkPlaceholder')}
              value={link}
              // Textul citit se leapada la orice schimbare a adresei: altfel ai
              // ramane cu articolul vechi sub adresa noua, fara sa se vada.
              onChange={e => schimba({ link: e.target.value, linkCitit: null })}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); citesteLinkul(); } }}
            />
            <button type="button" className="v2-btn-media mic"
              disabled={lucreaza || !link.trim()} onClick={citesteLinkul}>
              {t('compozitor2', 'inLinkCiteste')}
            </button>
          </div>
          {linkCitit && (
            <>
              {/* Textul INTREG, nu o bucata din el. Taierea la 400 de caractere
                  facea sa para ca articolul s-a citit pe jumatate: „tot
                  incomplet", cand de fapt ce lipsea era doar din previzualizare.
                  Caseta se deruleaza, deci incape oricat. */}
              <p className="v2-intrare-extras">
                {linkCitit.titlu && <b>{linkCitit.titlu}<br /></b>}
                {linkCitit.text}
              </p>
              <p className="v2-hint" style={{ margin: 0 }}>
                {t('compozitor2', linkCitit.taiat ? 'inLinkCititTaiat' : 'inLinkCitit',
                  { n: linkCitit.text.length })}
              </p>
              {/* Cand n-am gasit paragrafe si am luat toata pagina, omul trebuie
                  sa stie: acolo intra si meniuri, deci merita o privire. */}
              {linkCitit.metoda === 'tot' && (
                <p className="v2-hint" style={{ margin: 0 }}>{t('compozitor2', 'inLinkTotul')}</p>
              )}
            </>
          )}
          <textarea
            className="textarea"
            rows={2}
            placeholder={t('compozitor2', 'inContextPlaceholder')}
            value={text}
            onChange={e => schimba({ text: e.target.value })}
          />
          <p className="v2-hint">{t('compozitor2', 'inLinkHint')}</p>
        </>
      )}

      {fel === 'foto' && (
        <>
          <div className="v2-intrare-fisier">
            <label className="v2-btn-media mic">
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { preiaFoto(e.target.files[0]); e.target.value = ''; }} />
              {t('compozitor2', 'inAlegeFisier')}
            </label>
            {foto && <span className="v2-intrare-nume">{foto.nume}</span>}
            {foto && (
              <button type="button" className="v2-btn-media mic" onClick={() => schimba({ foto: null })}>
                {t('compozitor2', 'inScoate')}
              </button>
            )}
          </div>
          {foto && <img className="v2-intrare-poza" src={foto.previzualizare} alt={foto.nume} />}
          <textarea
            className="textarea"
            rows={2}
            placeholder={t('compozitor2', 'inContextPlaceholder')}
            value={text}
            onChange={e => schimba({ text: e.target.value })}
          />
          <p className="v2-hint">{t('compozitor2', 'inFotoHint')}</p>
        </>
      )}

      {fel === 'doc' && (
        <>
          <div className="v2-intrare-fisier">
            <label className="v2-btn-media mic">
              <input type="file" accept=".pdf,.docx,.txt" style={{ display: 'none' }}
                onChange={e => { preiaDoc(e.target.files[0]); e.target.value = ''; }} />
              {t('compozitor2', 'inAlegeFisier')}
            </label>
            {doc && <span className="v2-intrare-nume">{doc.nume}</span>}
            {doc && (
              <button type="button" className="v2-btn-media mic" onClick={() => schimba({ doc: null })}>
                {t('compozitor2', 'inScoate')}
              </button>
            )}
          </div>
          {doc && doc.text && (
            <p className="v2-intrare-extras">{doc.text}</p>
          )}
          <textarea
            className="textarea"
            rows={2}
            placeholder={t('compozitor2', 'inContextPlaceholder')}
            value={text}
            onChange={e => schimba({ text: e.target.value })}
          />
          <p className="v2-hint">{t('compozitor2', 'inDocHint')}</p>
        </>
      )}

      {lucreaza && <p className="v2-hint">{t('compozitor2', 'inSeCiteste')}</p>}
      {eroare && <p className="v2-eroare" style={{ marginTop: 8 }}>{eroare}</p>}
    </div>
  );
}
